import type { AnalyticsClient, AnalyticsProperties } from "./analytics-client";
import type { AppEvent } from "./events";

/**
 * The keys mutations declare themselves with. Exported so the hook and the
 * table below draw on the same constant: as free literals on both sides, a
 * rename at one hook silently stopped its event firing.
 */
export const MUTATION_KEYS = {
  draftCreate: ["draft-create"],
  inventionTitle: ["invention-title"],
  draftDelete: ["draft-delete"],
  draftTrash: ["draft-trash"],
  draftRestore: ["draft-restore"],
  draftStage: ["draft-stage"],
  clientCreate: ["client-create"],
  clientUpdate: ["client-update"],
  clientDelete: ["client-delete"],
  clientLogo: ["client-logo"],
  fileUpload: ["file-upload"],
  fileRetry: ["file-retry"],
  fileDelete: ["file-delete"],
  fileClassify: ["file-classify"],
  fileReplace: ["file-replace"],
  positionAdd: ["position-add"],
  positionAmend: ["position-amend"],
  positionConfirm: ["position-confirm"],
  positionAcknowledgeGaps: ["position-acknowledge-gaps"],
  positionRemove: ["position-remove"],
  positionMark: ["position-mark"],
  positionVerdict: ["position-verdict"],
  positionSuggest: ["position-suggest"],
  claimSetPropose: ["claim-set-propose"],
  claimEdit: ["claim-edit"],
  claimConfirm: ["claim-confirm"],
  claimUnconfirm: ["claim-unconfirm"],
  claimAdd: ["claim-add"],
  claimPromote: ["claim-promote"],
  claimDemote: ["claim-demote"],
  claimMove: ["claim-move"],
  claimRemove: ["claim-remove"],
  claimOrder: ["claim-order"],
  claimMarkerScheme: ["claim-marker-scheme"],
  figureBrief: ["figure-brief"],
  claimPreferences: ["claim-preferences"],
  claimSetLock: ["claim-set-lock"],
  noveltyRunStart: ["novelty-run-start"],
  noveltyAdmissionRule: ["novelty-admission-rule"],
  noveltyCommit: ["novelty-commit"],
  figureUpload: ["figure-upload"],
  figureLayout: ["figure-layout"],
  inventionElementAdd: ["invention-element-add"],
  inventionElementAmend: ["invention-element-amend"],
  inventionElementConfirm: ["invention-element-confirm"],
  inventionElementRemove: ["invention-element-remove"],
  inventionFallbackAdd: ["invention-fallback-add"],
  inventionFallbackAmend: ["invention-fallback-amend"],
  inventionFallbackRemove: ["invention-fallback-remove"],
  inventionElementName: ["invention-element-name"],
  inventionFallbackName: ["invention-fallback-name"],
  inventionSubject: ["invention-subject"],
  inventionElementRelate: ["invention-element-relate"],
  inventionElementUnrelate: ["invention-element-unrelate"],
  inventionStartExtraction: ["invention-start-extraction"],
  inventionSettle: ["invention-settle"],
  policiesAccept: ["policies-accept"],
  frameworkReplace: ["framework-replace"],
  frameworkConfirm: ["framework-confirm"],
  frameworkPropose: ["framework-propose"],
} as const;

interface ReportedMutation {
  event: AppEvent;
  /** Reads the few properties worth carrying off what the mutation was given. */
  properties?: (variables: unknown) => AnalyticsProperties | undefined;
}

/**
 * The mutations whose success is worth reporting, and the event each becomes.
 *
 * An allow-list rather than every mutation: an autosave firing on every
 * keystroke would drown the ones that mean something. Renames and deletes of a
 * draft are absent on purpose, because the dashboard reports those itself once
 * the server has confirmed them.
 */
const SUCCESS_EVENTS: Record<string, ReportedMutation> = {
  "draft-create": { event: "draft_created" },
  "client-create": { event: "client_created" },
  // One event with the kind alongside, rather than a name that claims more
  // than it knows: this mutation carries disclosures, claims, prior art and
  // walk-through clips alike.
  "file-upload": { event: "file_uploaded", properties: fileType },
  "file-retry": { event: "file_retried", properties: fileType },
  "file-delete": { event: "file_deleted" },
  "figure-upload": { event: "figure_uploaded" },
  "figure-layout": { event: "figure_layout_changed", properties: layoutChange },
  // Adding and confirming are the two acts that change what the invention is
  // held to be. Amending rewords something already counted, and a removal says
  // less than the count it leaves behind.
  "invention-element-add": { event: "invention_element_added" },
  "invention-element-confirm": { event: "invention_element_confirmed" },
  "invention-start-extraction": { event: "invention_extraction_started" },
  // The one act over the whole page: the attorney leaving with a settled invention.
  "invention-settle": { event: "invention_settled" },
  "novelty-run-start": { event: "novelty_run_started" },
  // The one act over the whole stage: the attorney leaving with every position
  // confirmed, which is what the next stage builds on.
  "novelty-commit": { event: "novelty_committed" },
  "policies-accept": { event: "policies_accepted" },
  // The one act over the whole stage: the attorney standing behind the plan the draft follows.
  "framework-confirm": { event: "dd_framework_confirmed" },
};

const UNNAMED = "unnamed";

function readString(variables: unknown, key: string): string | null {
  if (typeof variables !== "object" || variables === null) return null;
  const value: unknown = (variables as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
}

function fileType(variables: unknown): AnalyticsProperties | undefined {
  const type = readString(variables, "fileType");
  return type ? { file_type: type } : undefined;
}

/**
 * One mutation covers every edit to the figure layout, so the kind of edit is
 * the whole story: grouping, reordering, renaming, removing. The new title is
 * left behind, since an attorney names a group after the figure.
 */
function layoutChange(variables: unknown): AnalyticsProperties | undefined {
  const kind = readString(variables, "kind");
  return kind ? { change: kind } : undefined;
}

function labelOf(mutationKey: unknown): string | null {
  if (!Array.isArray(mutationKey)) return null;
  const [label] = mutationKey as unknown[];
  return typeof label === "string" ? label : null;
}

/** Read structurally rather than against ApiError, to keep this free of the
 * service layer. Anything carrying a numeric `status` is an HTTP failure. */
function statusOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null) return null;
  const status: unknown = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

export function reportMutationSuccess(
  analytics: AnalyticsClient,
  mutationKey: unknown,
  variables: unknown,
): void {
  const label = labelOf(mutationKey);
  const reported = label === null ? undefined : SUCCESS_EVENTS[label];
  if (!reported) return;
  analytics.capture(reported.event, reported.properties?.(variables));
}

/**
 * A failure its own code has already reported with what it knows, so the cache does not
 * count it a second time.
 */
export class ReportedFailure extends Error {}

/**
 * Reports that an action the app asked for did not happen.
 *
 * These are the failures the app already handles: it shows a message and
 * carries on, so nobody hears about them. Every failed mutation is reported,
 * named or not, because an unnamed failure is still a failure.
 *
 * The error message never travels. A rejected save can be rejected for quoting
 * the draft back, and the status is what says whether it was the server, the
 * network, or the request.
 */
export function reportMutationFailure(
  analytics: AnalyticsClient,
  mutationKey: unknown,
  error: unknown,
): void {
  if (error instanceof ReportedFailure) return;
  analytics.capture("client_error", {
    where: labelOf(mutationKey) ?? UNNAMED,
    status: statusOf(error),
  });
}
